/**
 * Privacy System — Context Provider + Hook + Badge
 *
 * Provides privacy style, conversation prompts, and shared progress
 * across the app. Adapts language and visibility based on household privacy style.
 */
import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { useAuth } from "../../App";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const PrivacyContext = createContext(null);

const STYLE_LABELS = {
  fully_shared: "Fully Shared",
  shared_personal: "Shared + Personal",
  separate_shared_goals: "Separate + Shared Goals",
  solo: "Solo Household",
};

const STYLE_DESCRIPTIONS = {
  fully_shared: "Everything is visible to both of you — one shared view.",
  shared_personal: "Shared finances together, with personal space for each.",
  separate_shared_goals: "Manage separately, align around shared goals.",
  solo: "Your personal financial system.",
};

export function PrivacyProvider({ children }) {
  const { household } = useAuth();
  const householdId = household?.household_id;

  const [privacyStyle, setPrivacyStyle] = useState("fully_shared");
  const [prompts, setPrompts] = useState({});
  const [sharedProgress, setSharedProgress] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    if (!householdId) return;
    try {
      const [privRes, promptRes, progressRes] = await Promise.all([
        fetch(`${API_URL}/api/households/${householdId}/privacy`, { credentials: "include" }),
        fetch(`${API_URL}/api/households/${householdId}/privacy/conversation-prompts`, { credentials: "include" }),
        fetch(`${API_URL}/api/households/${householdId}/privacy/shared-progress`, { credentials: "include" }),
      ]);
      if (privRes.ok) {
        const d = await privRes.json();
        setPrivacyStyle(d.privacy_style || "fully_shared");
      }
      if (promptRes.ok) {
        const d = await promptRes.json();
        setPrompts(d.prompts || {});
      }
      if (progressRes.ok) {
        setSharedProgress(await progressRes.json());
      }
    } catch {
      // keep defaults
    } finally {
      setLoading(false);
    }
  }, [householdId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const updateStyle = async (newStyle) => {
    if (!householdId) return;
    try {
      const res = await fetch(`${API_URL}/api/households/${householdId}/privacy`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ privacy_style: newStyle }),
      });
      if (res.ok) {
        setPrivacyStyle(newStyle);
        fetchAll(); // refresh prompts
      }
    } catch { /* silent */ }
  };

  const dismissPrompt = async (promptKey) => {
    if (!householdId) return;
    try {
      await fetch(`${API_URL}/api/households/${householdId}/privacy/dismiss-prompt`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ prompt_key: promptKey }),
      });
      setPrompts(prev => {
        const next = { ...prev };
        delete next[promptKey];
        return next;
      });
    } catch { /* silent */ }
  };

  const saveJournal = async (content, context, visibility = null, relatedId = null, type = "general", cycleId = null) => {
    if (!householdId) return null;
    try {
      const res = await fetch(`${API_URL}/api/households/${householdId}/journal`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ content, context, visibility, related_id: relatedId, type, cycle_id: cycleId }),
      });
      if (res.ok) return await res.json();
    } catch { /* silent */ }
    return null;
  };

  const isSolo = privacyStyle === "solo";
  const isShared = privacyStyle === "fully_shared";
  const hasPersonal = privacyStyle === "shared_personal";
  const isSeparate = privacyStyle === "separate_shared_goals";

  // Language helpers
  const together = isSolo ? "for you" : "together";
  const yourShared = isSolo ? "Your" : "Your shared";
  const youBoth = isSolo ? "you" : "both of you";

  return (
    <PrivacyContext.Provider value={{
      privacyStyle,
      loading,
      updateStyle,
      prompts,
      dismissPrompt,
      sharedProgress,
      saveJournal,
      isSolo, isShared, hasPersonal, isSeparate,
      together, yourShared, youBoth,
      label: STYLE_LABELS[privacyStyle] || "Fully Shared",
      description: STYLE_DESCRIPTIONS[privacyStyle] || "",
      refreshProgress: fetchAll,
    }}>
      {children}
    </PrivacyContext.Provider>
  );
}

export function usePrivacy() {
  const ctx = useContext(PrivacyContext);
  if (!ctx) return {
    privacyStyle: "fully_shared", loading: true, prompts: {},
    isSolo: false, isShared: true, hasPersonal: false, isSeparate: false,
    together: "together", yourShared: "Your shared", youBoth: "both of you",
  };
  return ctx;
}
